import type React from "react";
import { useState } from "react";

import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { useTranslation } from "@/translations";

import { USER_BULK_UPLOAD_FORM_ID } from "../constants";

type UserBulkUploadConfirmModalProps = ModalBaseProps & {
  isSubmitting: boolean;
};

export function UserBulkUploadConfirmModal({
  isOpened,
  onOpenChange,
  isSubmitting,
}: UserBulkUploadConfirmModalProps): React.ReactNode {
  const [isConfirmed, setIsConfirmed] = useState(false);

  const { t } = useTranslation();

  return (
    <Modal.Root
      title={t("page.user-bulk-upload.confirm-modal.title")}
      badgeComponent={<Badge variant="warning">{t("common.label.warning")}</Badge>}
      description={t("page.user-bulk-upload.confirm-modal.description")}
      showClose={!isSubmitting}
      shouldCloseOnEsc={!isSubmitting}
      shouldCloseOnClickOutside={!isSubmitting}
      {...{ isOpened, onOpenChange }}
      onOpenChange={(open) => {
        if (!open && isSubmitting) {
          return;
        }

        if (!open) {
          setIsConfirmed(false);
        }
        onOpenChange(open);
      }}
    >
      <Modal.Body>
        <Checkbox
          id="confirm-checkbox"
          name="isConfirmed"
          checked={isConfirmed}
          disabled={isSubmitting}
          onChange={(e) => setIsConfirmed(e.target.checked)}
          label={t("page.user-bulk-upload.form.confirm-checkbox.label")}
        />
      </Modal.Body>
      <Modal.Actions>
        <Modal.Close>
          <Button variant="outline" tone="primary" disabled={isSubmitting}>
            {t("common.action.cancel")}
          </Button>
        </Modal.Close>
        <Button isLoading={isSubmitting} disabled={!isConfirmed} type="submit" form={USER_BULK_UPLOAD_FORM_ID}>
          {t("common.action.confirm")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
